import React, { useEffect, useState } from 'react';
import { Button, DialogContent, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import SettingsIcon from '@material-ui/icons/Settings';
import { RadioButtonChecked, RadioButtonUnchecked } from '@material-ui/icons';
import tinycolor from 'tinycolor2';
import { isMobile } from 'react-device-detect';

import { focusOnFirstElement, getSubtitleFontSizeInPixelBasedOnScreenSize, joinClassNames } from '../../../util/util';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import TabSandbox from './TabSandbox';

type OptionTitleProps = {
	title: string;
};

const OptionTitle = ({ title }: OptionTitleProps) => {
	return <span style={{ fontSize: '0.875em', fontWeight: 'bold', marginBottom: 10, display: 'inline-block' }}>{title}</span>;
};

type ButtonOptionsSelectorProps = {
	title: string;
	options: number[];
	type: string;
	selectedOption?: number;
	container: any;
	onChange: (_selectedValue: any) => void;
};

const ButtonOptionsSelector = ({ title, type, options, selectedOption, container, onChange }: ButtonOptionsSelectorProps) => {
	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, option: number) => {
		if (event.key.includes('Arrow')) {
			const delta = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
			const currentIndex = options.indexOf(option ?? options[0]);
			const nextIndex = (currentIndex + delta + options.length) % options.length;
			const itemId = `${type}-${options[nextIndex]}-${nextIndex}`;
			event.preventDefault();
			event.stopPropagation();

			if (container.querySelector) {
				(container.querySelector(`#${itemId}`) as HTMLElement)?.focus();
			} else if (container.getElementById) {
				(container.getElementById(`${itemId}`) as HTMLElement)?.focus();
			}
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			event.stopPropagation();
			onChange(option);
		}
	};

	return (
		<div style={{ margin: '20px 10px' }}>
			<OptionTitle title={title} />
			<div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }} role="radiogroup" aria-label={title}>
				{options.map((option, index) => (
					<Button
						role="radio"
						id={`${type}-${option}-${index}`}
						tabIndex={selectedOption === option ? 0 : -1}
						key={option}
						variant="outlined"
						className="subtitle-menu-btn"
						style={{
							backgroundColor: selectedOption === option ? '#ffffff' : '#000000',
							color: selectedOption === option ? '#000000' : '#ffffff',
							border: '1px solid #ffffff',
							flex: 1,
							minWidth: 50,
							position: 'relative',
							overflow: 'visible',
						}}
						onClick={() => {
							onChange(option);
						}}
						onKeyDown={(e) => handleOnKeyDown(e, option)}
						aria-label={`${title} ${option}% ${selectedOption === option ? 'ticked' : ''}`}
					>
						{`${option}%`}
						<span aria-hidden="true" className="subtitle-swatch-tooltip">{`${title} ${option}%`}</span>
					</Button>
				))}
			</div>
		</div>
	);
};

type DropdownOptionsSelectorProps = {
	t: any;
	title: string;
	selectedOption: string;
	menuKey: string;
	onGoToDetailMenu: (_menuKey: string) => void;
};

const DropdownOptionsSelector = ({ t, title, selectedOption, menuKey, onGoToDetailMenu }: DropdownOptionsSelectorProps) => {
	return (
		<div style={{ margin: '20px 10px', fontSize: '0.875em' }} aria-label={`${title} ${t(selectedOption)}`}>
			<OptionTitle title={title} />
			<Button
				id={`${menuKey}-dropdown-button`}
				variant="text"
				onClick={() => onGoToDetailMenu(menuKey)}
				aria-label={`${t(selectedOption)} ${t('SUBTITLE_SETTING_MENU_GO_TO')} ${title} ${t('SUBTITLE_SETTING_MENU_DETAIL_MENU')}`}
				className="subtitle-menu-btn"
				style={{
					width: '100%',
					textTransform: 'none',
					color: 'white',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					position: 'relative',
					overflow: 'visible',
				}}
			>
				{t(selectedOption)}
				<KeyboardArrowRightIcon />
				<span aria-hidden="true" className="subtitle-swatch-tooltip">{title}</span>
			</Button>
		</div>
	);
};

type ColorOptionsSelectorProps = {
	t: any;
	title: string;
	colors: string[];
	type: string;
	selectedColor?: string;
	container: any;
	onChange: (_selectedValue: any) => void;
};

const ColorOptionsSelector = ({ t, title, colors, type, selectedColor, container, onChange }: ColorOptionsSelectorProps) => {
	const colorSize = window.innerWidth <= 600 ? 35 : 40;

	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, color: string) => {
		if (event.key.includes('Arrow')) {
			const delta = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
			const currentIndex = colors.indexOf(color ?? colors[0]);
			const nextIndex = (currentIndex + delta + colors.length) % colors.length;
			const itemId = `${type}-${COLOR_NAME[colors[nextIndex] as keyof typeof COLOR_NAME] || colors[nextIndex]}-${nextIndex}`.split(' ').join('-');

			if (container.querySelector) {
				(container.querySelector(`#${itemId}`) as HTMLElement)?.focus();
			} else if (container.getElementById) {
				(container.getElementById(`${itemId}`) as HTMLElement)?.focus();
			}
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			event.stopPropagation();
			onChange(color);
		}
	};
	return (
		<div style={{ margin: '20px 10px', fontSize: '0.875em' }}>
			<OptionTitle title={title} />
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} role="radiogroup" aria-label={title}>
				{colors.map((color: string, index) => {
					const label = `${title} ${t(TRANSLATION_NAMESPACE[color as keyof typeof TRANSLATION_NAMESPACE] || color)}`;
					return (
						<Button
							key={color}
							role="radio"
							tabIndex={selectedColor === color ? 0 : -1}
							id={`${type}-${COLOR_NAME[color as keyof typeof COLOR_NAME] || color}-${index}`.split(' ').join('-')}
							className="subtitle-color-option"
							style={{
								width: colorSize,
								height: colorSize,
								minWidth: colorSize,
								minHeight: colorSize,
								backgroundColor: color,
								borderRadius: 4,
								border: selectedColor === color ? `2px solid ${color === 'rgba(255, 255, 255, 1)' ? '#000000' : '#ffffff'}` : 'none',
								position: 'relative',
								overflow: 'visible',
							}}
							onClick={() => onChange(color)}
							onKeyDown={(e) => handleOnKeyDown(e, color)}
							aria-label={label}
							aria-checked={selectedColor === color ? 'true' : 'false'}
						>
							<span aria-hidden="true" className="subtitle-swatch-tooltip">{label}</span>
						</Button>
					);
				})}
			</div>
		</div>
	);
};

type RadioListItemsProps = {
	t: any;
	type: string;
	items: { name: string; value: string }[];
	selectedItem: string;
	container: any;
	onChange: (_selectedValue: any) => void;
	goToMainMenu: (_event: any) => void;
};

const RadioListItems = ({ t, type, items, selectedItem, container, onChange, goToMainMenu }: RadioListItemsProps) => {
	const handleOnKeyDown = (event: any, itemValue: string) => {
		if (event.key.includes('Arrow')) {
			const delta = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
			const currentIndex = items.findIndex(({ value }) => value === itemValue);
			const nextIndex = (currentIndex + delta + items.length) % items.length;
			const itemId = `${type}-${items[nextIndex]?.value}-${nextIndex}`.split(' ').join('-');
			if (container.querySelector) {
				(container.querySelector(`#${itemId}`) as HTMLElement)?.focus();
			} else if (container.getElementById) {
				(container.getElementById(`${itemId}`) as HTMLElement)?.focus();
			}
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			event.stopPropagation();
			onChange(itemValue);
		}
	};

	useEffect(() => {
		// Focus on the selected item when the component mounts
		focusOnFirstElement();
	}, []);

	return (
		<List role="menu" className="qplayer__setting-menu">
			<ListItem role="menuitem">
				<IconButton size="small" onClick={goToMainMenu} style={{ color: 'white' }} aria-label={t('LABEL_GO_BACK_BTN')}>
					<KeyboardArrowLeftIcon />
				</IconButton>
				<span style={{ fontSize: '0.875em' }}>{t(TRANSLATION_NAMESPACE[type as keyof typeof TRANSLATION_NAMESPACE] || type)}</span>
			</ListItem>
			{items.map(({ name, value }: any, index: number) => {
				return (
					<ListItem
						id={`${type}-${value}-${index}`.split(' ').join('-')}
						key={value}
						tabIndex={selectedItem === value ? 0 : -1}
						component={'div'}
						onClick={() => {
							onChange(value);
						}}
						onKeyDown={(e) => handleOnKeyDown(e, value)}
						aria-label={t(name)}
						role="menuitemradio"
						aria-checked={selectedItem === value ? 'true' : 'false'}
					>
						<ListItemIcon style={{ minWidth: '30px' }} aria-hidden="true">
							{selectedItem === value ? (
								<RadioButtonChecked htmlColor="#fff" aria-hidden="true" />
							) : (
								<RadioButtonUnchecked htmlColor="#fff" aria-hidden="true" />
							)}
						</ListItemIcon>
						<ListItemText
							primary={t(name)}
							disableTypography
							style={
								type === 'fontFamily'
									? {
										fontFamily: value,
										fontSize: '0.875em',
										fontVariant: value === 'Poppins' ? 'small-caps' : 'normal',
										cursor: 'pointer',
									}
									: {
										fontSize: '0.875em',
										cursor: 'pointer',
									}
							}
						/>
					</ListItem>
				);
			})}
		</List>
	);
};

type SubtitleCustomizedSettingsProps = {
	fontSize: number;
	fontFamily: string;
	fontColor: string;
	fontOpacity: number;
	characterEdgeStyle: string;
	backgroundColor: string;
	backgroundOpacity: number;
	windowColor: string;
	windowOpacity: number;
};

export const DEFAULT_SUBTITLE_CUSTOMIZED_SETTINGS = {
	fontSize: 100,
	fontFamily: 'Helvetica',
	fontColor: 'rgba(255, 255, 255, 1)',
	fontOpacity: 100,
	characterEdgeStyle: 'none',
	backgroundColor: 'rgba(0, 0, 0, 1)',
	backgroundOpacity: 75,
	windowColor: 'rgba(0, 0, 0, 1)',
	windowOpacity: 0,
};

const DEFAULT_FONT_FAMILIES = [
	{
		name: 'Monospace serif',
		value: 'Courier New',
	},
	{
		name: 'Monospace sans-serif',
		value: 'Deja Vu Sans Mono',
	},
	{
		name: 'Proportional serif',
		value: 'Times New Roman',
	},
	{
		name: 'SUBTITLE_SETTING_MENU_DEFAULT_FONT',
		value: 'Helvetica',
	},
	{
		name: 'Casual',
		value: 'Comic Sans MS',
	},
	{
		name: 'Cursive',
		value: 'Monotype Corsiva',
	},
	{
		name: 'Small caps',
		value: 'Poppins',
	},
];

const DEFAULT_CHARACTER_EDGE_STYLES = [
	{
		name: 'SUBTITLE_SETTING_MENU_EDGE_STYLE_NONE',
		value: 'none',
	},
	{
		name: 'SUBTITLE_SETTING_MENU_EDGE_STYLE_DROP_SHADOW',
		value: 'drop-shadow',
	},
	{
		name: 'SUBTITLE_SETTING_MENU_EDGE_STYLE_RAISED',
		value: 'raised',
	},
	{
		name: 'SUBTITLE_SETTING_MENU_EDGE_STYLE_DEPRESSED',
		value: 'depressed',
	},
	{
		name: 'SUBTITLE_SETTING_MENU_EDGE_STYLE_OUTLINE',
		value: 'outline',
	},
];

const TRANSLATION_NAMESPACE = {
	fontFamily: 'SUBTITLE_SETTING_MENU_FONT_FAMILY',
	characterEdgeStyle: 'SUBTITLE_SETTING_MENU_CHARACTER_EDGE_STYLE',
	'rgba(0, 0, 0, 1)': 'SUBTITLE_SETTING_MENU_COLOR_BLACK',
	'rgba(255, 242, 4, 1)': 'SUBTITLE_SETTING_MENU_COLOR_YELLOW',
	'rgba(8, 240, 35, 1)': 'SUBTITLE_SETTING_MENU_COLOR_GREEN',
	'rgba(1, 234, 255, 1)': 'SUBTITLE_SETTING_MENU_COLOR_CYAN',
	'rgba(0, 26, 255, 1)': 'SUBTITLE_SETTING_MENU_COLOR_BLUE',
	'rgba(255, 0, 170, 1)': 'SUBTITLE_SETTING_MENU_COLOR_MAGENTA',
	'rgba(255, 255, 255, 1)': 'SUBTITLE_SETTING_MENU_COLOR_WHITE',
};

const COLOR_NAME = {
	'rgba(0, 0, 0, 1)': 'black',
	'rgba(255, 242, 4, 1)': 'yellow',
	'rgba(8, 240, 35, 1)': 'green',
	'rgba(1, 234, 255, 1)': 'cyan',
	'rgba(0, 26, 255, 1)': 'blue',
	'rgba(255, 0, 170, 1)': 'magenta',
	'rgba(255, 255, 255, 1)': 'white',
};

type SubtitleSettingMenuProps = {
	languageSettingMenuRef: React.RefObject<any>;
	sideWindowOpen: boolean;
};

const useCustomStyle = makeStyles(() => {
	return {
		subtitleSettingMenu: (props: any) => ({
			'& .MuiDialog-container.MuiDialog-scrollPaper': {
				position: 'relative',
				width: props.sideWindowOpen ? '75%' : '100%',
			},
		}),
	};
});

const SubtitleSettingMenu = ({ languageSettingMenuRef, sideWindowOpen }: SubtitleSettingMenuProps) => {
	const { t } = useTranslation();
	const { openSettingMenu, screenType, configParams, setOpenSettingMenu } = useConfiguration();
	const styles = useCustomStyle({ sideWindowOpen });

	const subtitleSettingMenuRef = React.useRef<HTMLElement | null>(null);
	const [subSettingMenu, setSubSettingMenu] = useState<string>('');
	const [userSubtitleCustomizedSettings, setUserSubtitleCustomizedSettings] =
		useState<SubtitleCustomizedSettingsProps>(DEFAULT_SUBTITLE_CUSTOMIZED_SETTINGS);

	const { fontSize, fontFamily, fontColor, fontOpacity, characterEdgeStyle, backgroundColor, backgroundOpacity, windowColor, windowOpacity } =
		userSubtitleCustomizedSettings || DEFAULT_SUBTITLE_CUSTOMIZED_SETTINGS;

	const container = !configParams?.qplayerPreview ? configParams?.container ?? document : document;
	const qplayerWidth = configParams.container?.getBoundingClientRect()?.width || window.innerWidth;
	const isSmallScreen = qplayerWidth <= 600;

	const updateUserSubtitleSettings = (value: string, key: any) => {
		setUserSubtitleCustomizedSettings((prevSettings) => {
			const newSetting = { ...prevSettings, [key]: value };
			localStorage.setItem('userSubtitleCustomizedSettings', JSON.stringify(newSetting));
			return newSetting;
		});
	};

	const onBackButtonClicked = (event: any, triggerElemId?: string) => {
		if (subSettingMenu) {
			setSubSettingMenu('');
			if (triggerElemId && subtitleSettingMenuRef.current) {
				setTimeout(() => {
					const triggerElem = subtitleSettingMenuRef.current?.querySelector(`#${triggerElemId}`) as HTMLElement;
					triggerElem && triggerElem.focus();
				}, 50);
			}
		} else {
			setOpenSettingMenu({
				type: 'language',
				anchor: container.querySelector('.bottomControlbarLanguageMenu') as HTMLElement,
				isKeyboardTriggered: event.detail === 0,
			});
			languageSettingMenuRef.current?.setSelectedMenu('subtitle');
		}
	};

	useEffect(() => {
		let initUserSettings = DEFAULT_SUBTITLE_CUSTOMIZED_SETTINGS;
		try {
			initUserSettings = JSON.parse(localStorage.getItem('userSubtitleCustomizedSettings') || '');
		} catch (e) { }

		setUserSubtitleCustomizedSettings(initUserSettings);
	}, []);

	useEffect(() => {
		if (!openSettingMenu?.type) {
			setSubSettingMenu('');
		}
	}, [openSettingMenu]);

	useEffect(() => {
		if (!userSubtitleCustomizedSettings) {
			return;
		}

		const { fontSize, fontFamily, fontColor, fontOpacity, characterEdgeStyle, backgroundColor, backgroundOpacity, windowColor, windowOpacity } =
			userSubtitleCustomizedSettings;

		const calFontColor = tinycolor({ ...tinycolor(fontColor).toRgb(), a: fontOpacity / 100 }).toRgbString();
		const calBackgroundFontColor = tinycolor({ ...tinycolor(backgroundColor).toRgb(), a: backgroundOpacity / 100 }).toRgbString();
		const calBackgroundWindowColor = tinycolor({ ...tinycolor(windowColor).toRgb(), a: windowOpacity / 100 }).toRgbString();

    	const qplayerWidth = configParams.container?.getBoundingClientRect()?.width || window.innerWidth;
		const currentSubtitleFontSize = `${getSubtitleFontSizeInPixelBasedOnScreenSize(isMobile, qplayerWidth) * (fontSize / 100)}em`;
		if (document.getElementById(`userSubtitleStyling-${configParams.containerKey}`)) {
			document.getElementById(`userSubtitleStyling-${configParams.containerKey}`)?.remove();
		}

		const sheetSubtitleStyling = document.createElement('style');
		sheetSubtitleStyling.id = `userSubtitleStyling-${configParams.containerKey}`;

		const isSmallCap = fontFamily === 'Poppins' ? 'font-variant: small-caps !important;' : '';
		const isDropShadow = characterEdgeStyle === 'drop-shadow' ? 'text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5) !important;' : '';
		const isTextOutline =
			characterEdgeStyle === 'outline'
				? 'text-shadow: rgb(34, 34, 34) 0px 0px 1.88611px, rgb(34, 34, 34) 0px 0px 1.88611px, rgb(34, 34, 34) 0px 0px 1.88611px, rgb(34, 34, 34) 0px 0px 1.88611px, rgb(34, 34, 34) 0px 0px 1.88611px !important;'
				: '';
		const isDepressed =
			characterEdgeStyle === 'depressed' ? 'text-shadow: rgb(204, 204, 204) 1px 1px, rgb(34, 34, 34) -1px -1px !important;' : '';
		const isRaised = characterEdgeStyle === 'raised' ? 'text-shadow: rgb(34, 34, 34) 1px 1px, rgb(34, 34, 34) 2px 2px !important;' : '';

		const subtitleStyling = `[data-qplayer-key='${configParams.containerKey}'] div[data-internal-gobrain-box-configuration-modulename='Subtitles'] .subtitle-text:not(:empty) {background-color: ${calBackgroundWindowColor} !important;} [data-qplayer-key='${configParams.containerKey}'] div[data-internal-gobrain-box-configuration-modulename='Subtitles'] .subtitle-text:not(:empty) { font-family: ${fontFamily} !important; font-size: ${currentSubtitleFontSize}; color: ${calFontColor} !important;}`;

		const spanFont = `[data-qplayer-key='${configParams.containerKey}'] div[data-internal-gobrain-box-configuration-modulename='Subtitles'] .subtitle-text:not(:empty) span { font-family: ${fontFamily} !important; color: ${calFontColor} !important; background-color: ${calBackgroundFontColor} !important; ${isSmallCap} ${isDropShadow} ${isTextOutline} ${isDepressed} ${isRaised}}`;

		sheetSubtitleStyling.innerHTML = `${subtitleStyling} ${spanFont}`;
		document.head.appendChild(sheetSubtitleStyling);
	}, [userSubtitleCustomizedSettings, screenType, configParams.container, configParams.containerKey]);

	const bottomBar = container.querySelector('.bottomControlbar');
	const anchorElem = container.querySelector('.bottomControlbarLanguageMenu') ?? openSettingMenu?.anchor ?? container.querySelector('.bottomControlbar');
	const bottom = bottomBar ? bottomBar.getBoundingClientRect().height - 8 : 52;
	return (openSettingMenu?.type === 'subtitleSettings' ?
		<TabSandbox
			anchorElem={anchorElem as HTMLElement}
			className={joinClassNames('qplayer__setting-menu', 'qplayer__subtitle-setting', 'closeable-by-tab', 'qplayer__no-reset-focus', styles.subtitleSettingMenu)}
			style={{
				position: 'absolute',
				zIndex: 13000,
				top: 0,
				bottom: `${bottom}px`,
				right: 0,
				width: isSmallScreen ? '100%' : 393,
				maxHeight: `100%`,
			}}
			onMouseDown={(e: any) => {
				e.stopPropagation();
			}}
			wrapperRef={ref => subtitleSettingMenuRef.current = ref}
		>
			<>
				<DialogContent
					style={{
						padding: '0px 24px',
						height: '100%',
					}}
				>
					{!subSettingMenu && (
						<>
							<div
								style={{
									display: 'flex',
									justifyContent: 'flex-start',
									alignItems: 'center',
									margin: '12px 0',
								}}
							>
								<IconButton size="small" onClick={onBackButtonClicked} style={{ color: 'white' }} aria-label={t('LABEL_GO_BACK_BTN')}>
									<KeyboardArrowLeftIcon />
								</IconButton>
								<span style={{ fontSize: '0.875em' }}>{t('SUBTITLE_SETTING_MENU_TITLE')}</span>
							</div>
							<Divider tabIndex={-1} aria-hidden="true" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '0 5px 3px' }} />
							<ButtonOptionsSelector
								title={t('SUBTITLE_SETTING_MENU_FONT_SIZE')}
								options={[100, 120, 140, 160]}
								selectedOption={fontSize}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, 'fontSize')}
								container={container}
								type='fontSize'
							/>
							<DropdownOptionsSelector
								t={t}
								title={t('SUBTITLE_SETTING_MENU_FONT_FAMILY')}
								selectedOption={DEFAULT_FONT_FAMILIES.find(({ value }) => value === fontFamily)?.name ?? fontFamily}
								onGoToDetailMenu={setSubSettingMenu}
								menuKey={'fontFamily'}
							/>
							<ColorOptionsSelector
								t={t}
								title={t('SUBTITLE_SETTING_MENU_FONT_COLOR')}
								colors={[
									'rgba(0, 0, 0, 1)',
									'rgba(255, 242, 4, 1)',
									'rgba(8, 240, 35, 1)',
									'rgba(1, 234, 255, 1)',
									'rgba(0, 26, 255, 1)',
									'rgba(255, 0, 170, 1)',
									'rgba(255, 255, 255, 1)',
								]}
								selectedColor={fontColor}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, 'fontColor')}
								container={container}
								type="fontColor"
							/>
							<ButtonOptionsSelector
								title={t('SUBTITLE_SETTING_MENU_FONT_OPACITY')}
								options={[100, 75, 50, 25]}
								selectedOption={fontOpacity}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, 'fontOpacity')}
								container={container}
								type='fontOpacity'
							/>
							<DropdownOptionsSelector
								t={t}
								title={t('SUBTITLE_SETTING_MENU_CHARACTER_EDGE_STYLE')}
								selectedOption={
									DEFAULT_CHARACTER_EDGE_STYLES.find(({ value }) => value === characterEdgeStyle)?.name ?? characterEdgeStyle
								}
								onGoToDetailMenu={setSubSettingMenu}
								menuKey={'characterEdgeStyle'}
							/>
							<ColorOptionsSelector
								t={t}
								title={t('SUBTITLE_SETTING_MENU_BACKGROUND_COLOR')}
								colors={[
									'rgba(0, 0, 0, 1)',
									'rgba(255, 242, 4, 1)',
									'rgba(8, 240, 35, 1)',
									'rgba(1, 234, 255, 1)',
									'rgba(0, 26, 255, 1)',
									'rgba(255, 0, 170, 1)',
									'rgba(255, 255, 255, 1)',
								]}
								selectedColor={backgroundColor}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, 'backgroundColor')}
								container={container}
								type="bgColor"
							/>
							<ButtonOptionsSelector
								title={t('SUBTITLE_SETTING_MENU_BACKGROUND_OPACITY')}
								options={[100, 75, 50, 25, 0]}
								selectedOption={backgroundOpacity}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, 'backgroundOpacity')}
								container={container}
								type='bgOpacity'
							/>
							<ColorOptionsSelector
								t={t}
								title={t('SUBTITLE_SETTING_MENU_WINDOW_COLOR')}
								colors={[
									'rgba(0, 0, 0, 1)',
									'rgba(255, 242, 4, 1)',
									'rgba(8, 240, 35, 1)',
									'rgba(1, 234, 255, 1)',
									'rgba(0, 26, 255, 1)',
									'rgba(255, 0, 170, 1)',
									'rgba(255, 255, 255, 1)',
								]}
								selectedColor={windowColor}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, 'windowColor')}
								container={container}
								type="windowColor"
							/>
							<ButtonOptionsSelector
								title={t('SUBTITLE_SETTING_MENU_WINDOW_OPACITY')}
								options={[100, 75, 50, 25, 0]}
								selectedOption={windowOpacity}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, 'windowOpacity')}
								container={container}
								type='windowOpacity'
							/>
							<Divider tabIndex={-1} aria-hidden="true" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '0 5px 3px' }} />
							<Button
								variant="outlined"
								startIcon={<SettingsIcon style={{ width: 20, height: 20 }} fontSize='small'/>}
								style={{ color: 'white', textTransform: 'none' }}
								onClick={() => {
									setUserSubtitleCustomizedSettings(DEFAULT_SUBTITLE_CUSTOMIZED_SETTINGS);
									localStorage.setItem('userSubtitleCustomizedSettings', JSON.stringify(DEFAULT_SUBTITLE_CUSTOMIZED_SETTINGS));
								}}
								aria-label={t('SUBTITLE_SETTING_MENU_RESET_TO_DEFAULTS')}
							>
								{t('SUBTITLE_SETTING_MENU_RESET_TO_DEFAULTS')}
							</Button>
						</>
					)}

					{subSettingMenu === 'fontFamily' && (
						<>
							<RadioListItems
								t={t}
								items={DEFAULT_FONT_FAMILIES}
								selectedItem={fontFamily}
								type={subSettingMenu}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, subSettingMenu)}
								goToMainMenu={e => onBackButtonClicked(e, "fontFamily-dropdown-button")}
								container={container}
							/>
						</>
					)}

					{subSettingMenu === 'characterEdgeStyle' && (
						<>
							<RadioListItems
								t={t}
								items={DEFAULT_CHARACTER_EDGE_STYLES}
								selectedItem={characterEdgeStyle}
								type={subSettingMenu}
								onChange={(selectedValue: any) => updateUserSubtitleSettings(selectedValue, subSettingMenu)}
								goToMainMenu={e => onBackButtonClicked(e, "characterEdgeStyle-dropdown-button")}
								container={container}
							/>
						</>
					)}
				</DialogContent>
			</>
		</TabSandbox> : null
	);
};

export default SubtitleSettingMenu;
